import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { formatDisplayName } from 'utils/recipientSearch';
import { ScriptStepSubjectDef } from 'xm-constants/scriptStepPermissionDef';
import * as types from 'actions/recipientSearch/constant';
import {
  getAllUsersEntity,
  FETCH_ROLES_SUCCESS,
  FETCH_ROLES_START,
} from 'actions/permissionSearch/permissionSearch';

/**
 * DO NOT EXTEND THIS REDUCER.
 *
 * Fixing bugs in here is 100% okay.
 * However, if a story requests new functionality (e.g. a 3rd type other than the current Roles + Recipients)
 * please consider making a separate reducer. This one has become overloaded and difficult to reason about,
 * as such small changes risk unforeseen bugs to many existing components.
 */

/**
 * Sort 2 recipients first by their recipient types ("ROLE" < "GROUP" < "PERSON") on ascending order,
 * then sort by their display names on ascending order. Meant to be used for Array.prototype.sort
 * @param {Recipient} recip1 - With recipientType and name
 * @param {Recipient} recip2 - ^^
 *
 * @returns {Number} Negative if comes first; Positive if comes second; 0 if unchanged.
 */
export const sortRecipients = (recip1, recip2) => {
  // Because we prepend the ALL_USERS object at the front of JSON.data, don't sort it.
  // We don't support "ROLE" yet. Added here for forward compatibility.
  const order = [
    ScriptStepSubjectDef.role,
    ScriptStepSubjectDef.group,
    ScriptStepSubjectDef.person,
  ];

  const orderByType =
    order.indexOf(recip1.recipientType) - order.indexOf(recip2.recipientType);

  return (
    orderByType ||
    formatDisplayName(recip1).localeCompare(formatDisplayName(recip2))
  );
};

/**
 * Simulate the server filtering mechanism on the client, so "All Users" also participates in filtering.
 * For example, if I type "ad", "Adam" should be an option but "All Users" shouldn't.
 */
function isAllUsersOptionFilteredOut(search) {
  return !(
    search &&
    ((search.trim() === '' && search.length >= 2) ||
      'all users'.includes(search.trim().toLowerCase()))
  );
}

/**
 * If we don't use permissionSearchMode (which means we're searching for recipients instead),
 * simply yank out "allUser" from the payload before merging with state.
 * Alternatively, if we do choose to do permissionSearchMode, on top of yanking "allUser" out as well, we will also:
 * 1. Add it to the front of json.data if it's not filtered out by searching
 * 2. Sort the recipients
 * 3. Increment (if necessary) count and total
 *
 * @param {Object} payload
 * @returns {Object} A processed payload object to be merged into the state tree.
 */
const processPayload = payload => {
  const { intl, permissionSearchMode, startIndex, ...otherPayloadProps } =
    payload;

  const { count, data, search, total } = otherPayloadProps;

  if (!permissionSearchMode) {
    return {
      ...payload,
      pagingTotal: total,
    };
  }

  const isAllUsersFilteredOut = isAllUsersOptionFilteredOut(search);

  if (startIndex !== 0) {
    return { ...payload, hasAllUser: !isAllUsersFilteredOut };
  }

  const sortedRecipients = [...data].sort(sortRecipients);
  const allUser = getAllUsersEntity({ intl });
  return {
    ...otherPayloadProps,
    data: isAllUsersFilteredOut
      ? sortedRecipients
      : [allUser, ...sortedRecipients],
    count: isAllUsersFilteredOut ? count : count + 1,
    total: isAllUsersFilteredOut ? total : total + 1,
    pagingTotal: total,
    hasAllUser: !isAllUsersFilteredOut,
    startIndex,
    allUser,
  };
};

const defaultPageObj = {
  data: [],
  hasAllUser: false,
  results: [],
  total: 0,
  offset: 0,
  stopIndex: 0,
  isLoading: false,
  selectedRecipients: [],
  recipientType: 'ALL',
  searchType: 'CONTAINS',
  flipPanels: false,
  excludedRecipients: [],
  hasFetchError: false,
  roles: [],
};

const defaultState = {
  generic: {
    ...defaultPageObj,
  },
  // TESTPAGE: {
  //   ...defaultPageObj,
  // },
};

const toRecipient = role => ({
  id: role.id,
  recipientType: ScriptStepSubjectDef.role,
  targetName: role.name,
  displayName: role.name,
});

const transformToRecipients = roles => roles.map(r => toRecipient(r));

const sameSearchType = (type1, type2) => isEqual(type1, type2);

const mergeRoles = (state, roles) => {
  const { hasAllUser, allUser, data: sData, total: totalRecipients } = state;

  const combined = [
    ...(hasAllUser ? [allUser] : []),
    ...roles,
    ...sData.filter(
      recipient =>
        recipient.recipientType !== 'ALL_USERS' &&
        recipient.recipientType !== 'ROLE',
    ),
  ];

  return {
    ...state,
    data: combined,
    total: totalRecipients + roles.length,
    roles,
  };
};

/**
 * @param sSearch the store value
 * @param pSearch the page from the server
 * @return true if the pSearch and sSearch are from the same search but for a different paging offset
 */
const handleSameSearch = (
  sData,
  sSearch,
  sSearchType,
  sRecipientType,
  sTotal,
  pData,
  pSearch,
  pSearchType,
  pRecipientType,
  pTotal,
  pStartIndex,
  pStopIndex,
) => {
  // Consider payload searchType and payload recipientType as "same" if they are undefined in case
  // the consumer uses SEARCH_RECIPIENT_SET_RECIPIENT_TYPE and SEARCH_RECIPIENT_SET_SEARCH_TYPE:
  const isSameSearchType =
    sameSearchType(sSearchType, pSearchType) || pSearchType === undefined;
  const isRecipientSearchType =
    sameSearchType(sRecipientType, pRecipientType) ||
    pRecipientType === undefined;

  if (
    sSearch === pSearch &&
    isRecipientSearchType &&
    isSameSearchType &&
    sTotal > 0
  ) {
    let data = sData;
    if (pStartIndex === sData.length) {
      data = sData.concat(pData);
    } else if (pStartIndex < sData.length && pStopIndex >= sData.length) {
      data = sData.slice(0, pStartIndex).concat(pData);
    } else if (pStartIndex < sData.length && pStopIndex < sData.length) {
      data = sData
        .slice(0, pStartIndex)
        .concat(pData)
        .concat(sData.slice(pStopIndex, sData.length));
    } else {
      data = [...sData];
      pData.forEach((datum, i) => {
        data[pStartIndex + i] = datum;
      });
    }

    return data;
  }

  return null;
};

const mergePageableResultsWithRoles = (state, payload) => {
  const {
    allUser,
    search: sSearch,
    recipientType: sRecipientType,
    searchType: sSearchType,
    data: sData,
    pagingTotal,
    roles,
  } = state;

  const {
    search: pSearch,
    recipientType: pRecipientType,
    searchType: pSearchType,
    data: pData,
    startIndex: pStartIndex,
    stopIndex: pStopIndex,
    total: pTotal,
    hasAllUser,
  } = payload;

  const offsetToRecipients = roles.length + (hasAllUser ? 1 : 0);
  const recipientData = sData.slice(offsetToRecipients);
  let data = handleSameSearch(
    recipientData,
    sSearch,
    sSearchType,
    sRecipientType,
    pagingTotal,
    pData,
    pSearch,
    pSearchType,
    pRecipientType,
    pTotal,
    pStartIndex,
    pStopIndex,
  );

  if (data) {
    const allUserId = allUser?.id;
    data = [
      ...roles,
      ...(data.length > 0 && data[0].id === allUserId ? data.slice(1) : data),
    ];
    if (hasAllUser) {
      data.unshift(allUser);
    }
    return {
      ...payload,
      data,
      total:
        hasAllUser && data.length === 1 ? 1 : pagingTotal + offsetToRecipients,
      pagingTotal,
    };
  }
  return mergeRoles({ ...payload, hasAllUser, allUser }, roles);
};

const mergePageableResults = (state, payload) => {
  const {
    search: sSearch,
    recipientType: sRecipientType,
    searchType: sSearchType,
    data: sData,
    total: sTotal,
    roles = [],
  } = state;

  const {
    hasAllUser,
    search: pSearch,
    recipientType: pRecipientType,
    searchType: pSearchType,
    data: pData,
    startIndex: pStartIndex,
    stopIndex: pStopIndex,
    total: pTotal,
  } = payload;

  if (roles.length || hasAllUser) {
    return mergePageableResultsWithRoles(state, payload);
  }

  const data = handleSameSearch(
    sData,
    sSearch,
    sSearchType,
    sRecipientType,
    sTotal,
    pData,
    pSearch,
    pSearchType,
    pRecipientType,
    pTotal,
    pStartIndex,
    pStopIndex,
  );
  return data
    ? {
        ...payload,
        data,
      }
    : {
        ...payload,
      };
};

export default function recipientSearchReducer(state = defaultState, action) {
  const { type, payload, meta } = action;
  const page = get(meta, 'page', 'generic');

  const stateCopy = !state[page]
    ? { ...state, [page]: defaultState.generic }
    : { ...state };

  switch (type) {
    case types.INITIALIZE:
      return {
        ...defaultState,
        [page]: defaultState.generic,
      };
    case types.SEARCH_RECIPIENT_SET_RECIPIENT_TYPE: {
      return {
        ...stateCopy,
        [page]: {
          ...stateCopy[page],
          recipientType: payload,
        },
      };
    }
    case types.SEARCH_RECIPIENT_SET_SEARCH_TYPE: {
      return {
        ...stateCopy,
        [page]: {
          ...stateCopy[page],
          searchType: payload,
        },
      };
    }
    case types.SEARCH_GROUPS_START:
    case types.SEARCH_PEOPLE_START:
    case types.SEARCH_RECIPIENT_START:
      return {
        ...stateCopy,
        [page]: {
          ...stateCopy[page],
          isLoading: true,
        },
      };
    case types.SEARCH_RECIPIENT_SUCCESS: {
      // xm API doesn't support "All Users" as an option in Usage Permission dialog. Fake it here.
      const processedPayload = processPayload(payload);
      // Meant to be the same as the 2 switch cases below; we only changed the payload if necessary.
      return {
        ...stateCopy,
        [page]: {
          ...stateCopy[page],
          ...mergePageableResults(stateCopy[page], processedPayload),
          isLoading: false,
          hasFetchError: false,
        },
      };
    }

    case types.SEARCH_GROUPS_SUCCESS:
    case types.SEARCH_PEOPLE_SUCCESS:
    case types.RESET_RECIPIENT_SEARCH_RESULTS: {
      const pageState = stateCopy[page];
      if (payload.permissionSearchMode) {
        pageState.roles = [];
      }
      const processedPayload = {
        ...payload,
        search: pageState.search,
        hasAllUser: false,
      };

      return {
        ...stateCopy,
        [page]: {
          ...pageState,
          ...mergePageableResults(pageState, processedPayload),
          isLoading: false,
          hasFetchError: false,
        },
      };
    }
    case types.SEARCH_GROUPS_FAIL:
    case types.SEARCH_PEOPLE_FAIL:
    case types.SEARCH_RECIPIENT_FAIL:
      return {
        ...stateCopy,
        [page]: {
          ...stateCopy[page],
          results: [],
          hasFetchError: true,
          isLoading: false,
        },
      };
    case types.SEARCH_RECIPIENT_SET_SELECTED_RECIPIENT_LIST:
      return {
        ...stateCopy,
        [page]: {
          ...stateCopy[page],
          selectedRecipients: payload,
        },
      };
    case types.SEARCH_RECIPIENT_SET_FLIP_PANELS:
      return {
        ...stateCopy,
        [page]: {
          ...stateCopy[page],
          flipPanels: payload,
        },
      };
    case types.SEARCH_RECIPIENT_SET_EXCLUDED_RECIPIENT_LIST:
      return {
        ...stateCopy,
        [page]: {
          ...stateCopy[page],
          excludedRecipients: payload,
        },
      };
    case types.RESET_RECIPIENT_SEARCH_STATE:
      return {
        ...defaultState,
      };

    case FETCH_ROLES_START:
      return {
        ...stateCopy,
        [page]: {
          ...stateCopy[page],
          allUser: getAllUsersEntity({ intl: payload.intl }),
          search: payload.search,
          hasAllUser:
            payload.permissionSearchMode &&
            !isAllUsersOptionFilteredOut(payload.search),
        },
      };
    case FETCH_ROLES_SUCCESS: {
      const pageState = stateCopy[page];
      return {
        ...stateCopy,
        [page]: {
          ...pageState,
          ...mergeRoles(pageState, transformToRecipients(payload.data)),
        },
      };
    }
    default:
      return state;
  }
}
